import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { API_URL } from '../../../environment';

interface CreateEditModalProps {
  show: boolean;
  onHide: () => void;
  modalType: 'etapa' | 'funil';
  isEdit?: boolean; // true para edição, false para criação
  initialName?: string;
  currentFunnel?: string; // Usado apenas para o caso de etapa
  closerAtivo?: boolean; // Prop to control visibility of prompt field
  idClienteOverride?: string | null; // 🆕 Prop opcional para Admin
  onSaved?: () => void; // Callback chamado após salvar com sucesso para o pai refazer fetch
}


const CreateEditModal: React.FC<CreateEditModalProps> = ({
  show,
  onHide,
  modalType,
  isEdit = false, // valor padrão false se não for passado
  initialName = '',
  currentFunnel,
  closerAtivo = false, // Default to false
  idClienteOverride = null, // 🆕 Default to null
  onSaved,
}) => {
  const [name, setName] = useState(initialName);
  // Usa o override se existir, senão pega do localStorage
  const id_cliente = idClienteOverride || localStorage.getItem("id_cliente");
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [saveMessage, setSaveMessage] = useState<string>("");
  // const [closerAtivo, setCloserAtivo] = useState(false); // Removed local state
  const [instrucoesFunil, setInstrucoesFunil] = useState("");
  
  // --- Estados para Gerenciamento de Cards (apenas edição de funil) ---
  const [allCardGroups, setAllCardGroups] = useState<any[]>([]);
  const [funnelId, setFunnelId] = useState<number | null>(null);
  const [expandedGroup, setExpandedGroup] = useState<number | null>(null);

  const [fullScreenField, setFullScreenField] = useState<{
    title: string;
    value: string;
    onSave: (val: string) => void;
  } | null>(null);

  const openFullScreen = (title: string, value: string, onSave: (val: string) => void) => {
    setFullScreenField({ title, value, onSave });
  };

  const fieldTypes: { [key: string]: string } = {
    text: "Texto",
    number: "Número",
    date: "Data",
    currency: "Moeda",
    select: "Seleção",
    file: "Arquivo"
  };

  useEffect(() => {
    setName(initialName);
  }, [initialName]);

  // Carregar grupos e ID do funil ao abrir para edição
  useEffect(() => {
    if (modalType === "funil" && isEdit && initialName && id_cliente) {
      // 1. Buscar ID do Funil pelo nome
      fetch(`${API_URL}/pegarpaginas`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id_cliente }),
      })
      .then(res => res.json())
      .then(data => {
        const funil = data.find((f: any) => f.page === initialName);
        if (funil) {
          fetch(`${API_URL}/card/funnels?id_cliente=${id_cliente}`)
            .then(r => r.json())
            .then(cardFunnels => {
              const match = cardFunnels.find((cf: any) => cf.nome === initialName);
              if (match) setFunnelId(match.id);
            });
            
          setInstrucoesFunil(funil.instrucoes || "");
        }
      });

      // 2. Buscar todos os grupos de cards E seus campos
      const fetchGroupsWithFields = async () => {
        try {
          const res = await fetch(`${API_URL}/card/groups?id_cliente=${id_cliente}`);
          const groups = await res.json();
          
          const groupsWithFields = await Promise.all(groups.map(async (g: any) => {
            const fieldsRes = await fetch(`${API_URL}/card/fields?id_grupo=${g.id}`);
            const fields = fieldsRes.ok ? await fieldsRes.json() : [];
            return { ...g, fields };
          }));
          
          setAllCardGroups(groupsWithFields);
        } catch (error) {
          console.error("Erro ao carregar grupos e campos:", error);
        }
      };
      
      fetchGroupsWithFields();
    }
  }, [modalType, isEdit, initialName, id_cliente]);

  // Função para vincular/desvincular grupo
  const handleToggleGroup = async (groupId: number, isLinked: boolean) => {
    if (!funnelId) return;

    try {
      await fetch(`${API_URL}/card/groups/${groupId}/toggle_funnel`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          id_funil: funnelId,
          active: !isLinked
        })
      });
      
      // Atualiza lista local
      setAllCardGroups(prev => prev.map(g => {
        if (g.id !== groupId) return g;

        const currentIds = g.funnel_ids || [];
        if (isLinked) {
          // Remover ID
          return { ...g, funnel_ids: currentIds.filter((id: number) => id !== funnelId) };
        } else {
          // Adicionar ID
          return { ...g, funnel_ids: [...currentIds, funnelId] };
        }
      }));
    } catch (err) {
      console.error("Erro ao atualizar grupo:", err);
    }
  };




  const handleSave = async () => {
    if (!id_cliente) {
      console.error("ID do cliente não encontrado");
      return;
    }
    setIsSaving(true);
    setSaveMessage(""); // Limpa mensagens anteriores
  
    let response: Response | undefined; // Declaramos response aqui
  
    try {
      if (modalType === 'funil') {
        if (isEdit) {
          const payload = {
            id_cliente,
            old_nome_funil: initialName, // nome antigo
            nome_funil: name.trim(),      // novo nome
          };
          console.log("Payload para editar funil:", payload);
          response = await fetch(`${API_URL}/editarfunil`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          });
          if (!response.ok) throw new Error("Erro ao editar funil");
        } else {
          response = await fetch(`${API_URL}/criarfunil`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              nome_funil: name.trim(),
              id_cliente,
            }),
          });
          if (!response.ok) throw new Error("Erro ao criar funil");
        }
      } else if (modalType === 'etapa') {
        if (!currentFunnel) {
          console.error("Funil atual não definido para a criação da etapa");
          setIsSaving(false);
          return;
        }
        if (isEdit) {
          const payload = {
            id_cliente,
            nome_funil: currentFunnel,
            old_nome_etapa: initialName, // etapa antiga
            nome_etapa: name.trim(),      // nova etapa
          };
          console.log("Payload para editar etapa:", payload);
          response = await fetch(`${API_URL}/editaretapa`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          });
          console.log("Response editar etapa:", response);
          if (!response.ok) throw new Error("Erro ao editar etapa");
        } else {
          response = await fetch(`${API_URL}/criaretapa`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              nome_funil: currentFunnel,
              nome_etapa: name.trim(),
              id_cliente,
            }),
          });
          if (!response.ok) throw new Error("Erro ao criar etapa");
        }
      }
  
      // Verifica se response foi definida
      if (!response) {
        throw new Error("Nenhuma resposta retornada da API.");
      }
  
      // Se a requisição foi bem-sucedida, buscamos a mensagem de sucesso da API (opcional)
      const responseData = await response.json();
      const msg = responseData.message || "Salvo com sucesso!";
      setSaveMessage(msg);

      // 🔹 Se for funil com closer ativo, salvar instruções
      if (modalType === "funil" && closerAtivo && instrucoesFunil.trim() !== "") {
        await fetch(`${API_URL}/salvarprompts_funil`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id_cliente,
            nome_funil: name.trim(),
            instrucoes_funil: instrucoesFunil,
          }),
        });
      }

      // Notifica o pai imediatamente para refazer fetch — antes do delay de 2s
      onSaved?.();

      // Aguarda 2 segundos para mostrar a mensagem, depois fecha o modal
      setTimeout(() => {
        onHide();
        setIsSaving(false);
        setSaveMessage("");
      }, 2000);
  
    } catch (error) {
      console.error(error);
      setSaveMessage("Erro ao salvar");
      setIsSaving(false);
    }
  };

  
  
  

  return (
    <>
    <Modal show={show} onHide={onHide} centered dialogClassName="tp-modal">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className={`ti ti-${modalType === 'funil' ? 'filter' : 'list-check'} me-2`} />
          {modalType === 'funil'
            ? isEdit ? 'Editar Funil' : 'Criar Funil'
            : isEdit ? 'Editar Etapa' : 'Criar Etapa'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {modalType === 'etapa' && currentFunnel && (
          <div className="tp-form-group">
            <label className="tp-form-label">Funil</label>
            <div className="tp-input-wrapper">
              <i className="ti ti-filter tp-input-icon" />
              <Form.Control type="text" value={currentFunnel} readOnly />
            </div>
          </div>
        )}
        <div className="tp-form-group">
          <label className="tp-form-label">
            {modalType === 'funil' ? 'Nome do Funil' : 'Nome da Etapa'}
          </label>
          <div className="tp-input-wrapper">
            <i className={`ti ti-${modalType === 'funil' ? 'filter' : 'list-check'} tp-input-icon`} />
            <Form.Control
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={modalType === 'funil' ? 'Ex: Vendas B2B' : 'Ex: Qualificação'}
            />
          </div>
        </div>

        {modalType === "funil" && isEdit && funnelId && (
          <div className="tp-card-groups">
            <h6 className="tp-card-groups-title">Vincular Grupos de Cards</h6>
            <div className="tp-card-groups-list">
              {allCardGroups
                .filter(g => !g.padrao)
                .map(group => {
                  const isLinked = group.funnel_ids?.includes(funnelId);
                  const isExpanded = expandedGroup === group.id;

                  return (
                    <div key={group.id} className="tp-card-group-item">
                      <div className="tp-card-group-header">
                        <div
                          className="tp-card-group-name"
                          onClick={() => setExpandedGroup(isExpanded ? null : group.id)}
                        >
                          <i className={`ti ti-chevron-${isExpanded ? 'down' : 'right'}`} />
                          {group.nome}
                        </div>
                        <button
                          className={`tp-link-btn ${isLinked ? 'linked' : 'unlinked'}`}
                          onClick={() => handleToggleGroup(group.id, !!isLinked)}
                        >
                          <i className={`ti ti-${isLinked ? 'minus' : 'plus'} me-1`} />
                          {isLinked ? "Desvincular" : "Vincular"}
                        </button>
                      </div>

                      {isExpanded && (
                        <div className="tp-card-group-fields">
                          {group.fields && group.fields.length > 0 ? (
                            <ul>
                              {group.fields.map((f: any) => (
                                <li key={f.id}>
                                  <strong>{f.label}</strong> ({fieldTypes[f.tipo] || f.tipo})
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <span className="text-muted" style={{ fontSize: '0.8rem' }}>Sem campos neste grupo.</span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              {allCardGroups.filter(g => !g.padrao).length === 0 && (
                <p className="text-center text-muted" style={{ fontSize: '0.82rem', padding: '16px 0' }}>Nenhum grupo de card disponível.</p>
              )}
            </div>
            <p className="text-muted" style={{ fontSize: '0.75rem', marginTop: '8px' }}>
              Grupos "Global" aparecem automaticamente. Vincule grupos específicos aqui.
            </p>
          </div>
        )}

        {modalType === "funil" && closerAtivo && (
          <div className="tp-form-group" style={{ marginTop: '18px' }}>
            <div className="d-flex align-items-center gap-2 mb-1">
              <label className="tp-form-label mb-0">Instruções do Funil (Closer)</label>
              <button
                type="button"
                className="tp-expand-btn"
                onClick={() => openFullScreen("Instruções do Funil", instrucoesFunil, setInstrucoesFunil)}
                style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer' }}
              >
                <i className="ti ti-arrows-maximize" />
              </button>
            </div>
            <Form.Control
              as="textarea"
              rows={12}
              className="tp-textarea-instructions"
              value={instrucoesFunil}
              onChange={(e) => setInstrucoesFunil(e.target.value)}
              placeholder="Descreva as instruções gerais do funil..."
            />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button className="tp-btn-cancel" onClick={onHide} disabled={isSaving}>Cancelar</button>
        <button className="tp-btn-save" onClick={handleSave} disabled={isSaving}>
          {isSaving ? "Salvando..." : "Salvar"}
        </button>
        {saveMessage && <span style={{ marginLeft: '10px', fontSize: '0.82rem', color: '#10b981' }}>{saveMessage}</span>}
      </Modal.Footer>
    </Modal>

    {fullScreenField && (
      <Modal show={true} onHide={() => setFullScreenField(null)} centered fullscreen dialogClassName="tp-fullscreen-modal">
        <Modal.Header closeButton>
          <Modal.Title>{fullScreenField.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <textarea
            className="form-control"
            style={{ height: '100%', fontSize: '1.1rem', resize: 'none' }}
            value={fullScreenField.value}
            onChange={(e) => setFullScreenField({ ...fullScreenField, value: e.target.value })}
          />
        </Modal.Body>
        <Modal.Footer>
          <button className="tp-btn-cancel" onClick={() => setFullScreenField(null)}>Cancelar</button>
          <button className="tp-btn-save" onClick={() => {
            fullScreenField.onSave(fullScreenField.value);
            setFullScreenField(null);
          }}>Salvar</button>
        </Modal.Footer>
      </Modal>
    )}
    </>
  );
};

export default CreateEditModal;
